import {
  CurrencyEnum,
  FormInput,
  FormInputSearch,
  Tooltip,
  centsToUnits,
  units,
} from '@systemeio/ui'
import { createTag, removeTag, updateTag } from 'modules/tags/api/tags-api'
import TagStatisticSkeleton from 'modules/tags/components/tag-statistic-skeleton'
import { TagsLoco } from 'modules/tags/constants/tags-loco'
import { useTags } from 'modules/tags/hooks/use-tags'
import { TagFilterInterface } from 'modules/tags/types/tag-filter-interface'
import {
  TagCreateInterface,
  TagErrorsInterface,
  TagInterface,
} from 'modules/tags/types/tag-interface'
import { getTagStatistic } from 'modules/tags/utils/get-tag-statistic'
import { NextPage } from 'next'
import React, { useState } from 'react'
import { CreateDataType } from 'shared/components/create-modal'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination'
import { OrderEnum } from 'shared/types/pagination-interface'
import { currencyFormat } from 'shared/utils/currency-format'
import { fetchWithErrors } from 'shared/utils/fetch-with-errors'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'

const defaultValues: TagCreateInterface = {
  name: '',
}

const defaultErrors: TagErrorsInterface = {
  name: '',
}

const defaultFilters: TagFilterInterface = { searchBy: '' }

const Tag: NextPage = () => {
  const { user, isWsAssistant } = useUser()
  const { filter, setFilter, setPagination, pagination, setLimit, limit } =
    useFilterPagination(defaultFilters)
  const [errors, setErrors] = useState<TagErrorsInterface>(defaultErrors)
  const { t } = useLocoTranslation()

  const { tags, tagsStatistics, mutate, counter, revalidateTagsModule } = useTags({
    limit,
    pagination,
    filter,
  })

  const columns: ColumnDefinitionInterface<TagInterface>[] = [
    {
      key: 'name',
      header: t('dashboard.tag.tag_name'),
      widthPercent: 30,
      onRender: tag => (
        <div className="truncate">
          {isWsAssistant ? (
            <span className={'w-full'}>{tag.name}</span>
          ) : (
            <LinkWithoutPrefetch
              className="primary-link w-full"
              href={`/contacts?filter=%5B%7B"searchValue"%3A"${tag.id}"%2C"compareOperation"%3A"IN_LIST"%2C"searchField"%3A"tag"%7D%5D`}
            >
              {tag.name}
            </LinkWithoutPrefetch>
          )}
        </div>
      ),
    },
    {
      key: 'statistics.today',
      header: (
        <Tooltip mode={'hover'} label={t('dashboard.tag.sub_today')} wrapperClassName="w-full">
          <span className={'truncate'}>{t('dashboard.tag.today')}</span>
        </Tooltip>
      ),
      widthPercent: 15,
      onRender: tag => (
        <>{getTagStatistic(tag, tagsStatistics)?.today ?? <TagStatisticSkeleton />}</>
      ),
    },
    {
      key: 'statistics.yesterday',
      header: (
        <Tooltip mode={'hover'} label={t('dashboard.tag.sub_yesterday')} wrapperClassName="w-full">
          <span className={'truncate'}>{t('dashboard.tag.yesterday')}</span>
        </Tooltip>
      ),
      widthPercent: 15,
      onRender: tag => (
        <>{getTagStatistic(tag, tagsStatistics)?.yesterday ?? <TagStatisticSkeleton />}</>
      ),
    },
    {
      key: 'statistics.totalAdded',
      header: (
        <Tooltip mode={'hover'} label={t('dashboard.tag.sub_total')} wrapperClassName="w-full">
          <span className={'truncate'}>{t('dashboard.tag.total')}</span>
        </Tooltip>
      ),
      widthPercent: 15,
      onRender: tag => (
        <>{getTagStatistic(tag, tagsStatistics)?.totalAdded ?? <TagStatisticSkeleton />}</>
      ),
    },
    {
      key: 'statistics.totalRemoved',
      header: (
        <Tooltip mode={'hover'} label={t('dashboard.tag.sub_removed')} wrapperClassName="w-full">
          <span className={'truncate'}>{t('dashboard.tag.unsubscribed')}</span>
        </Tooltip>
      ),
      widthPercent: 15,
      onRender: tag => (
        <>{getTagStatistic(tag, tagsStatistics)?.totalRemoved ?? <TagStatisticSkeleton />}</>
      ),
    },
    {
      key: 'statistics.salesAmount',
      header: t('dashboard.tag.total_sales'),
      widthPercent: 20,
      onRender: tag => {
        const statistic = getTagStatistic(tag, tagsStatistics)
        if (!statistic) return <TagStatisticSkeleton />
        return (
          <>
            {statistic.salesAmount.length !== 0 ? (
              <Tooltip
                mode={'hover'}
                label={
                  <div className={'flex flex-col gap-1.5'}>
                    <span className={'text-text-primary font-bold'}>
                      {t('dashboard.tag.sales_before_refunds')}
                    </span>
                    {statistic.salesAmount.map((el, idx) => (
                      <span key={idx}>
                        {currencyFormat(
                          centsToUnits(el.refunded),
                          el.currency,
                          user?.dashboardLocale,
                        )}
                      </span>
                    ))}
                  </div>
                }
              >
                <div className={'flex flex-col gap-1.5'}>
                  {statistic.salesAmount.map((el, idx) => (
                    <span key={idx}>
                      {currencyFormat(
                        centsToUnits(el.notRefunded),
                        el.currency,
                        user?.dashboardLocale,
                      )}
                    </span>
                  ))}
                </div>
              </Tooltip>
            ) : (
              currencyFormat(units(0), CurrencyEnum.EURO, user?.dashboardLocale)
            )}
          </>
        )
      },
    },
    {
      key: 'statistics.averageSales',
      header: t('dashboard.tag.sales_per_contact'),
      widthPercent: 20,
      onRender: tag => {
        const statistic = getTagStatistic(tag, tagsStatistics)
        if (!statistic) return <TagStatisticSkeleton />
        return (
          <>
            {statistic.averageSales.length !== 0 ? (
              <Tooltip
                mode={'hover'}
                label={
                  <div className={'flex flex-col gap-1.5'}>
                    <span className={'text-text-primary font-bold'}>
                      {t('dashboard.tag.sales_before_refunds')}
                    </span>
                    {statistic.averageSales.map((el, idx) => (
                      <span key={idx}>
                        {currencyFormat(
                          centsToUnits(el.refunded),
                          el.currency,
                          user?.dashboardLocale,
                        )}
                      </span>
                    ))}
                  </div>
                }
              >
                <div className={'flex flex-col gap-1.5'}>
                  {statistic.averageSales.map((el, idx) => (
                    <span key={idx}>
                      {currencyFormat(
                        centsToUnits(el.notRefunded),
                        el.currency,
                        user?.dashboardLocale,
                      )}
                    </span>
                  ))}
                </div>
              </Tooltip>
            ) : (
              currencyFormat(units(0), CurrencyEnum.EURO, user?.dashboardLocale)
            )}
          </>
        )
      },
    },
  ]

  const onRemoveTag = async (tag: TagInterface) => {
    await removeTag(tag.id)
    revalidateTagsModule(true)
    setPagination({ order: OrderEnum.next })
  }

  const onCreateTag = async (data: CreateDataType<TagCreateInterface>) => {
    await fetchWithErrors(async () => {
      await createTag(data)
      revalidateTagsModule(true)
      setPagination({ order: OrderEnum.next })
    }, setErrors)
  }

  const onUpdateTag = async (tag: TagInterface, newData: TagCreateInterface) => {
    await fetchWithErrors(async () => {
      await updateTag(tag.id, newData)
      await mutate(data => {
        if (data) {
          return {
            items: data.items.map(item => {
              if (item.id === tag.id) item = { ...item, ...newData }
              return item
            }),
            hasMore: data.hasMore,
          }
        }
      }, false)
      revalidateTagsModule()
    }, setErrors)
  }

  const onModalClose = () => setErrors(defaultErrors)

  return (
    <ListingLayout
      counter={counter}
      data={tags}
      title={t('dashboard.tag.title_plural')}
      type={'compact'}
      filterData={{
        filterer: { filter, setFilter, defaultFilters },
        paginator: { pagination, setPagination },
        limiter: { limit, setLimit },
        filterable: [
          {
            field: 'searchBy',
            onRender: (value, onChange) => (
              <FormInputSearch
                key={'tag-search-filter'}
                onClear={() => onChange('')}
                value={value}
                onChange={e => onChange(e.target.value)}
              />
            ),
          },
        ],
      }}
      createData={{
        onCreate: data => onCreateTag(data),
        defaultValues: defaultValues,
        caption: t('dashboard.tag.title'),
        onAfterLeave: onModalClose,
        requiredFields: ['name'],
        creatable: [
          {
            field: 'name',
            onRender: (value, onChange) => (
              <FormInput
                key="tag-name-create"
                label={t('dashboard.tag.tag_name')}
                placeholder={t('global.name')}
                value={value}
                onChange={e => onChange(e.target.value)}
                maxLength={256}
                error={errors.name}
              />
            ),
          },
        ],
      }}
    >
      <Table
        data={tags}
        columns={columns}
        caption={t('dashboard.tag.title')}
        limiter={{ limit, setLimit }}
        paginator={{ pagination, setPagination }}
        actions={{
          settings: {
            onModalClose: onModalClose,
            editable: [
              {
                field: 'name',
                onRender: (value, onChange) => (
                  <FormInput
                    key="tag-name-edit"
                    label=""
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    error={errors.name}
                  />
                ),
              },
            ],
            onEdit: (tag, data) => onUpdateTag(tag, data),
          },
          remove: {
            onChange: tag => onRemoveTag(tag),
          },
        }}
      />
    </ListingLayout>
  )
}

export const getServerSideProps = getBaseServerSideProps(TagsLoco, 'header.top_menu.tags')

export default Tag
